/**
 * Skills (Blueprint v2 P8): reusable, stack-aware capability packs. A skill
 * declares triggers, the stacks it applies to, a model hint, a system-prompt
 * fragment, and an output contract. `selectSkills` routes a request to the
 * matching skills (the deterministic composer core); every codegen skill emits a
 * `{ path: contents }` JSON file map, which `parseFileMap` validates so the write
 * stage is deterministic and path-safe.
 */
import { resolve, sep } from 'node:path';

export interface SkillExample {
  readonly prompt: string;
  readonly note?: string;
}

export interface Skill {
  readonly name: string;
  readonly description: string;
  /** Lowercase substrings that route a request to this skill. */
  readonly triggers: readonly string[];
  /** Stacks this skill is tuned for (informational; empty = any). */
  readonly appliesToStacks: readonly string[];
  /** Preferred tier for this skill's generations. */
  readonly modelHint: 'fx-pro' | 'fx-fast' | 'fx-mini';
  /** The system-prompt fragment this skill contributes. */
  readonly system: string;
  /** Output contract — codegen skills emit a `{ path: contents }` file map. */
  readonly outputContract: 'file-map';
  readonly examples: readonly SkillExample[];
}

const FILE_MAP_CONTRACT =
  'OUTPUT CONTRACT: respond with ONLY a single JSON object mapping each repo-relative POSIX file path ' +
  'to that file\'s FULL contents as a string. No prose, no markdown, no code fences. ' +
  'Example: {"src/app/page.tsx":"export default function Page(){...}"}';

export const BUILTIN_SKILLS: readonly Skill[] = [
  {
    name: 'frontend-nextjs-tailwind',
    description: 'Next.js (App Router) + TypeScript + Tailwind UI',
    triggers: ['next.js', 'nextjs', 'next ', 'react', 'tailwind', 'frontend', 'ui ', 'component', 'page ', 'app router'],
    appliesToStacks: ['Next.js', 'React'],
    modelHint: 'fx-pro',
    system: [
      'You are a senior frontend engineer building Next.js (App Router) + TypeScript + Tailwind CSS.',
      'Default to React Server Components; add "use client" only when interactivity requires it.',
      'Style with Tailwind utility classes; keep components small, typed, and accessible (semantic HTML, aria).',
      'Co-locate route files under app/, shared UI under components/. No inline styles, no any.',
    ].join(' '),
    outputContract: 'file-map',
    examples: [{ prompt: 'build a chat app in Next.js with a message list and input' }],
  },
  {
    name: 'backend-api',
    description: 'Typed HTTP API (Fastify / Express / NestJS / Go / Python profiles)',
    triggers: ['api', 'endpoint', 'route', 'backend', 'server', 'rest', 'graphql', 'fastify', 'express', 'nestjs', 'controller', 'handler'],
    appliesToStacks: ['Fastify', 'Express', 'NestJS'],
    modelHint: 'fx-pro',
    system: [
      'You are a senior backend engineer building typed HTTP APIs.',
      'Match the project framework (Fastify/Express/NestJS/Go/Python). Validate inputs at the boundary,',
      'return a consistent error envelope, keep handlers thin and business logic in testable services.',
      'No secrets in code; read config from the environment.',
    ].join(' '),
    outputContract: 'file-map',
    examples: [{ prompt: 'add a REST endpoint POST /messages backed by a service' }],
  },
  {
    name: 'project-structuring',
    description: 'Scaffold project structure, config, and conventions',
    triggers: ['scaffold', 'structure', 'set up', 'setup', 'bootstrap', 'monorepo', 'new project', 'layout', 'organize', 'bootstrapping'],
    appliesToStacks: [],
    modelHint: 'fx-mini',
    system: [
      'You are a senior engineer setting up project structure.',
      'Propose a clean directory layout, the minimal config files, and naming conventions suited to the stack.',
      'Prefer convention over configuration; include a short README and sensible .gitignore.',
    ].join(' '),
    outputContract: 'file-map',
    examples: [{ prompt: 'scaffold a TypeScript monorepo with pnpm workspaces' }],
  },
];

/**
 * Route a request to the skills whose triggers match (the deterministic composer
 * core; a Flash-OFF intent normalizer can refine this later). Falls back to
 * `project-structuring` when nothing else matches.
 */
export function selectSkills(prompt: string, skills: readonly Skill[] = BUILTIN_SKILLS): Skill[] {
  const p = prompt.toLowerCase();
  const matched = skills.filter((s) => s.triggers.some((t) => p.includes(t)));
  if (matched.length > 0) return matched;
  const fallback = skills.find((s) => s.name === 'project-structuring');
  return fallback ? [fallback] : [];
}

/** Compose the system prompts of the selected skills plus the file-map contract. */
export function composeSkillSystems(skills: readonly Skill[]): string {
  const parts = skills.map((s) => `# Skill: ${s.name}\n${s.system}`);
  parts.push(FILE_MAP_CONTRACT);
  return parts.join('\n\n');
}

/* -------------------------------------------------------------------------- */
/* Output contract: { path: contents } file map                              */
/* -------------------------------------------------------------------------- */

export interface FileMapEntry {
  readonly path: string;
  readonly contents: string;
}

export interface FileMapParse {
  readonly files: FileMapEntry[];
  readonly errors: string[];
}

/** True when `p` is a safe repo-relative path (no absolute / drive / UNC / `..`). */
export function isSafeRepoPath(p: string): boolean {
  if (!p || p.startsWith('/') || p.startsWith('\\')) return false;
  if (/^[a-zA-Z]:/.test(p)) return false; // drive-letter absolute
  const segments = p.split(/[\\/]/);
  return !segments.some((s) => s === '..' || s === '');
}

function extractJsonObject(raw: string): Record<string, unknown> | undefined {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) return undefined;
  try {
    return JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Deterministically parse a skill's `{ path: contents }` output into validated,
 * path-safe file entries. Unsafe paths and non-string contents are rejected into
 * `errors` rather than written.
 */
export function parseFileMap(raw: string): FileMapParse {
  const json = extractJsonObject(raw);
  if (!json) return { files: [], errors: ['no JSON object found in skill output'] };
  const files: FileMapEntry[] = [];
  const errors: string[] = [];
  for (const [path, contents] of Object.entries(json)) {
    if (typeof contents !== 'string') {
      errors.push(`'${path}': contents is not a string`);
      continue;
    }
    if (!isSafeRepoPath(path)) {
      errors.push(`'${path}': unsafe path rejected (absolute / .. / UNC)`);
      continue;
    }
    files.push({ path: path.split('\\').join('/'), contents });
  }
  return { files, errors };
}

/**
 * Confirm a parsed file entry resolves inside `root` (defense-in-depth over
 * {@link isSafeRepoPath}). The actual write is performed by the caller.
 */
export function resolvesInRepo(root: string, relPath: string): boolean {
  const base = resolve(root);
  const abs = resolve(base, relPath);
  return abs === base || abs.startsWith(base + sep);
}
